import axios from "axios";
import { pushToast } from "../utils/toastBus";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000"
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Automatic toasts for every mutating (non-GET) request, in addition to
// whatever inline error/success text each page already shows — same
// message, both places. Scoped to POST/PUT/PATCH/DELETE only: GET requests
// include a lot of silent background polling (live location, job board,
// order status) that shouldn't surface a toast on every transient failure.
const MUTATING_METHODS = ["post", "put", "patch", "delete"];

api.interceptors.response.use(
  (response) => {
    const method = response.config?.method;
    if (method && MUTATING_METHODS.includes(method)) {
      const msg = response.data?.message;
      if (typeof msg === "string" && msg) {
        pushToast({ type: "success", text: msg });
      }
    }
    return response;
  },
  (error) => {
    const method = error.config?.method;
    if (method && MUTATING_METHODS.includes(method)) {
      const msg = error.response?.data?.error || error.message || "Something went wrong. Please try again.";
      pushToast({ type: "error", text: msg });
    }
    return Promise.reject(error);
  }
);

// Public
export const getLaunchedCities = () => api.get("/public/launched-cities");
export const getPublicPlans = () => api.get("/public/plans");
export const reverseGeocodeServer = (lat, lng) => api.get(`/public/reverse-geocode?lat=${lat}&lng=${lng}`);

// Auth — role is "user" | "rider" (admins are seeded directly, not self-registered)
export const registerAccount = (role, data) => api.post(`/auth/${role}/register`, data);
export const loginAccount = (role, data) => api.post(`/auth/${role}/login`, data);

// User — profile & preferred rider
export const getMyProfile = () => api.get("/users/me");
export const updateMyProfile = (data) => api.patch("/users/me/profile", data);
export const changeMyPassword = (currentPassword, newPassword) => api.patch("/users/me/password", { currentPassword, newPassword });
export const setPreferredRider = (riderId) => api.patch("/users/me/preferred-rider", { riderId });
export const getNearbyRiders = (lat, lng) => api.get(`/users/nearby-riders?lat=${lat}&lng=${lng}`);
export const getSavedContacts = () => api.get("/users/me/saved-contacts");
export const addSavedContact = (data) => api.post("/users/me/saved-contacts", data);
export const deleteSavedContact = (id) => api.delete(`/users/me/saved-contacts/${id}`);

// User — wallet
export const getMyWallet = () => api.get("/users/me/wallet");
export const getWalletTransactions = () => api.get("/users/me/wallet/transactions");
export const initiateTopup = (amount, gateway, email) => api.post("/users/me/wallet/topup", { amount, gateway, email });
export const verifyTopup = (reference, gateway, amount, transactionId) => api.post("/users/me/wallet/verify", { reference, gateway, amount, transactionId });

// User — FASTSEND Plus / Saver Subscription
export const initiateSubscription = (gateway, email, plan = "plus") => api.post("/users/me/subscription/initiate", { gateway, email, plan });
export const verifySubscription = (reference, gateway, plan = "plus") => api.post("/users/me/subscription/verify", { reference, gateway, plan });
export const subscribeViaWallet = (plan = "plus") => api.post("/users/me/subscription/wallet", { plan });

// User — orders
export const previewQuote = (data) => api.post("/orders/quote", data);
export const placeOrder = (data) => api.post("/orders", data);
export const getOrder = (id) => api.get(`/orders/${id}`);
export const generateShareLink = (id) => api.post(`/orders/${id}/share-link`);
export const getOrderRoute = (id, originLat, originLng) => {
  const params = new URLSearchParams();
  if (originLat != null && originLng != null) { params.set("originLat", originLat); params.set("originLng", originLng); }
  const qs = params.toString();
  return api.get(`/orders/${id}/route${qs ? `?${qs}` : ""}`);
};
export const getMyOrders = () => api.get("/users/me/orders");
export const submitOrderRating = (orderId, rating, comment) => api.post(`/orders/${orderId}/rating`, { rating, comment });
export const cancelOrder = (orderId, reason, note, images) => api.post(`/orders/${orderId}/cancel`, { reason, note, images });
export const requestRevokeRA = (orderId) => api.post(`/orders/${orderId}/ra/revoke-request`);
export const decideRevokeRA = (orderId, approve) => api.patch(`/orders/${orderId}/ra/revoke-decision`, { approve });
export const verifyOrderPayment = (orderId, reference, gateway) => api.post(`/orders/${orderId}/verify-payment`, { reference, gateway });

// Real-time Chat
export const getOrderMessages = (orderId) => api.get(`/orders/${orderId}/messages`);
export const sendOrderMessage = (orderId, text) => api.post(`/orders/${orderId}/messages`, { text });
export const markOrderMessagesRead = (orderId) => api.patch(`/orders/${orderId}/messages/read`);

// Public order share link — no login, used by a receiver who has no account.
export const getPublicOrder = (token) => api.get(`/public/orders/${token}`);
export const payPublicOrder = (token, gateway, email) => api.post(`/public/orders/${token}/pay`, { gateway, email });
export const requestPublicPaymentMethodChange = (token, method) => api.post(`/public/orders/${token}/request-payment-method`, { method });
export const verifyPublicOrderPayment = (token, reference, gateway) => api.post(`/public/orders/${token}/verify-payment`, { reference, gateway });
// The slip endpoint requires auth, so it can't be a plain <a href> (a direct
// browser navigation sends no Authorization header). Fetch it as a blob
// through axios instead, which carries the Bearer token via the interceptor.
export const downloadAcknowledgementSlip = (orderId) => api.get(`/orders/${orderId}/acknowledgement-slip.pdf`, { responseType: "blob" });

// Uploads — package photos, RA signature/photo/ID (multipart)
export const uploadFile = (file, folder = "misc") => {
  const form = new FormData();
  form.append("file", file);
  return api.post(`/uploads?folder=${folder}`, form, { headers: { "Content-Type": "multipart/form-data" } });
};

// Insurance Claims
export const submitClaim = (data) => api.post("/claims", data);
export const getMyClaims = () => api.get("/claims/my-claims");
export const getAdminClaims = (status) => api.get(`/claims/admin${status ? `?status=${status}` : ""}`);
export const updateClaimStatus = (id, status, resolutionNotes) => api.patch(`/claims/admin/${id}`, { status, resolutionNotes });

// Support Tickets
export const submitSupportTicket = (data) => api.post("/support", data);
export const getMySupportTickets = () => api.get("/support/my-tickets");
export const reopenSupportTicket = (id) => api.patch(`/support/my-tickets/${id}/reopen`);
export const getAdminSupportTickets = (status) => api.get(`/support/admin${status ? `?status=${status}` : ""}`);
export const respondToSupportTicket = (id, status, adminResponse) => api.patch(`/support/admin/${id}`, { status, adminResponse });

// B2B Merchant Tools
export const generateB2BApiKey = (companyName, billingTerms) => api.post("/api/v1/b2b/api-key", { companyName, billingTerms });
export const getB2BInvoices = () => api.get("/api/v1/b2b/invoices");
export const getB2BOrders = () => api.get("/api/v1/b2b/orders");
export const getB2BApiLogs = () => api.get("/api/v1/b2b/api-logs");
export const getB2BOrderStatus = (orderId, apiKey) => api.get(`/api/v1/b2b/orders/${orderId}`, { headers: { "X-API-Key": apiKey } });
export const generateB2BInvoice = (startDate, endDate) => api.post("/api/v1/b2b/invoices/generate", { startDate, endDate });
export const payB2BInvoice = (invoiceId, method) => api.post(`/api/v1/b2b/invoices/${invoiceId}/pay`, { method });
export const verifyInvoicePayment = (invoiceId, reference, gateway) => api.post(`/api/v1/b2b/invoices/${invoiceId}/verify-payment`, { reference, gateway });
export const submitB2BOrder = (order, apiKey) => api.post("/api/v1/b2b/orders", order, { headers: { "X-API-Key": apiKey } });
export const submitBulkOrders = (orders, apiKey) => api.post("/api/v1/b2b/orders/bulk", { orders }, { headers: { "X-API-Key": apiKey } });
export const getB2BOrderShareLink = (orderId, apiKey) => api.get(`/api/v1/b2b/orders/${orderId}/share-link`, { headers: { "X-API-Key": apiKey } });
export const setB2BWebhook = (webhookUrl) => api.patch("/api/v1/b2b/webhook", { webhookUrl });

// Rider
export const setOnlineStatus = (isOnline) => api.patch("/riders/me/status", { isOnline });
export const updateLocation = (lat, lng) => api.patch("/riders/me/location", { lat, lng });
export const getRiderActiveOrders = () => api.get("/riders/me/active-orders");
export const updateOrderStatus = (orderId, status, verification, extra = {}) =>
  api.patch(`/orders/${orderId}/status`, { status, verification, ...extra });
export const completeStop = (orderId, stopIndex) => api.patch(`/orders/${orderId}/stops/${stopIndex}/complete`, {});
export const raiseOrderIssue = (orderId, reason, note, images) => api.post(`/orders/${orderId}/issue`, { reason, note, images });
export const requestVerificationOverride = (orderId, note) => api.post(`/orders/${orderId}/request-verification-override`, { note });
export const requestOrderPaymentMethod = (orderId, method) => api.post(`/orders/${orderId}/request-payment-method`, { method });

// Rider — offer accept/pass + job board
export const getPendingOffer = () => api.get("/riders/me/pending-offer");
export const acceptOffer = (orderId) => api.post(`/riders/me/orders/${orderId}/accept`);
export const passOffer = (orderId) => api.post(`/riders/me/orders/${orderId}/pass`);
export const decidePaymentChangeRequest = (orderId, decision) => api.patch(`/riders/me/orders/${orderId}/payment-change-decision`, { decision });
export const switchToCash = (orderId) => api.post(`/riders/me/orders/${orderId}/switch-to-cash`);
export const getJobBoard = () => api.get("/riders/me/job-board");
export const claimJob = (orderId) => api.post(`/riders/me/orders/${orderId}/claim`);

export const getRiderHistory = () => api.get("/riders/me/history");
export const getRiderStats = () => api.get("/riders/me/stats");
export const getRiderProfileCompleteness = () => api.get("/riders/me/profile-completeness");
export const getRiderWallet = () => api.get("/riders/me/wallet");
export const setRiderPayoutAccount = (data) => api.patch("/riders/me/payout-account", data);
export const updateRiderSweepSchedule = (sweepSchedule, preferredAmount) => api.patch("/riders/me/sweep-schedule", { sweepSchedule, preferredAmount });
export const getBankList = (provider) => api.get(`/riders/banks?provider=${provider}`);
export const resolveAccountName = (provider, bankCode, accountNumber) =>
  api.get(`/riders/resolve-account?provider=${provider}&bankCode=${bankCode}&accountNumber=${accountNumber}`);
export const createRiderVirtualAccount = (provider) => api.post("/riders/me/virtual-account", { provider });
export const deleteRiderVirtualAccount = () => api.delete("/riders/me/virtual-account");
export const simulateRiderVirtualAccountFunding = (amount) => api.post("/riders/me/virtual-account/simulate-funding", { amount });
export const withdrawRiderWallet = (amount) => api.post("/riders/me/withdraw", amount ? { amount } : {});
export const topupRiderWallet = (amount, gateway, redirectUrl) => api.post("/riders/me/topup", { amount, gateway, redirectUrl });
export const verifyRiderTopup = (reference, gateway, transactionId) => api.post("/riders/me/topup/verify", { reference, gateway, transactionId });
export const changeRiderPassword = (currentPassword, newPassword) => api.patch("/riders/me/password", { currentPassword, newPassword });
export const getRiderProfileChangeRequest = () => api.get("/riders/me/profile-change-request");
export const submitRiderProfileChangeRequest = (data) => api.post("/riders/me/profile-change-request", data);
export const getPaymentChannels = () => api.get("/public/payment-channels");

// Admin
export const listPendingRiders = () => api.get("/admin/riders/pending");
export const listApprovedRiders = (city) => api.get(`/admin/riders/approved${city ? `?city=${city}` : ""}`);
export const getAdminRider = (riderId) => api.get(`/admin/riders/${riderId}`);
export const setRiderApproval = (riderId, status) => api.patch(`/admin/riders/${riderId}/approval`, { status });
export const toggleRiderBlock = (riderId, isBlocked, reason) => api.patch(`/admin/riders/${riderId}/block`, { isBlocked, reason });
export const adminResetRiderPassword = (riderId, newPassword) => api.patch(`/admin/riders/${riderId}/reset-password`, { newPassword });
export const setRiderPriority = (riderId, priorityStatus) => api.patch(`/admin/riders/${riderId}/priority`, { priorityStatus });
export const adminSetRiderPayoutAccount = (riderId, data) => api.patch(`/admin/riders/${riderId}/payout-account`, data);
export const adminDeleteRiderVirtualAccount = (riderId) => api.delete(`/admin/riders/${riderId}/virtual-account`);
export const adminCreditRiderWallet = (riderId, amount, reason, reference) => api.post(`/admin/riders/${riderId}/credit-wallet`, { amount, reason, reference });
export const getProfileChangeRequests = () => api.get("/admin/riders/profile-change-requests");
export const decideProfileChangeRequest = (riderId, approve) => api.patch(`/admin/riders/${riderId}/profile-change-request`, { approve });
export const getActiveOrders = (city, status) => {
  const params = new URLSearchParams();
  if (city) params.set("city", city);
  if (status) params.set("status", status);
  const qs = params.toString();
  return api.get(`/admin/orders${qs ? `?${qs}` : ""}`);
};
export const getAdminRevenue = () => api.get("/admin/revenue");
export const getOrderIssues = (status) => api.get(`/admin/orders/issues${status ? `?status=${status}` : ""}`);
export const resolveOrderIssue = (orderId, resolutionNote) => api.patch(`/admin/orders/${orderId}/issue/resolve`, { resolutionNote });
export const getVerificationOverrides = (status) => api.get(`/admin/orders/verification-overrides${status ? `?status=${status}` : ""}`);
export const decideVerificationOverride = (orderId, approve, decisionNote) => api.patch(`/admin/orders/${orderId}/verification-override`, { approve, decisionNote });
export const reassignOrder = (orderId, riderId) => api.patch(`/admin/orders/${orderId}/assign`, { riderId });
export const adminSetOrderPaymentMethod = (orderId, paymentMethod) => api.patch(`/admin/orders/${orderId}/payment-method`, { paymentMethod });
export const adminCompleteOrder = (orderId) => api.patch(`/admin/orders/${orderId}/complete`);
export const setTruckPrice = (orderId, total) => api.patch(`/admin/orders/${orderId}/truck-price`, { total });
export const getPricingSettings = () => api.get("/admin/pricing");
export const updatePricingSettings = (data) => api.patch("/admin/pricing", data);
export const getPricingHistory = () => api.get("/admin/pricing/history");

// Admin — User management
export const getAdminUsers = (params) => api.get("/admin/users", { params });
export const getAdminUser = (id) => api.get(`/admin/users/${id}`);
export const blockUser = (id, isBlocked, reason) => api.patch(`/admin/users/${id}/block`, { isBlocked, reason });
export const adminResetUserPassword = (id, newPassword) => api.patch(`/admin/users/${id}/reset-password`, { newPassword });
export const adminApproveB2B = (id, data) => api.patch(`/admin/users/${id}/b2b-approve`, data);
export const adminRevokeB2B = (id) => api.patch(`/admin/users/${id}/b2b-revoke`);
export const adminApproveCorporate = (id, data) => api.patch(`/admin/users/${id}/corporate-approve`, data);
export const adminRevokeCorporate = (id) => api.patch(`/admin/users/${id}/corporate-revoke`);
export const getAdminAgents = (status) => api.get(`/admin/agents${status ? `?status=${status}` : ""}`);
export const adminApproveAgent = (id) => api.patch(`/admin/users/${id}/agent-approve`);
export const adminRevokeAgent = (id) => api.patch(`/admin/users/${id}/agent-revoke`);
export const getAdminFleetOwners = (status) => api.get(`/admin/fleet-owners${status ? `?status=${status}` : ""}`);
export const adminApproveFleetOwner = (id) => api.patch(`/admin/users/${id}/fleet-owner-approve`);
export const adminRevokeFleetOwner = (id) => api.patch(`/admin/users/${id}/fleet-owner-revoke`);
export const assignRiderFleetOwner = (riderId, fleetOwnerId) => api.patch(`/admin/riders/${riderId}/fleet-owner`, { fleetOwnerId });
export const adminSetBillingMode = (id, billingMode) => api.patch(`/admin/users/${id}/billing-mode`, { billingMode });
export const adminListInvoices = (params) => api.get("/admin/invoices", { params });
export const adminMarkInvoicePaid = (invoiceId, note) => api.patch(`/admin/invoices/${invoiceId}/mark-paid`, { note });

// Admin — Admin account management
export const getAdminList = () => api.get("/admin/admins");
export const createAdminAccount = (data) => api.post("/admin/admins", data);
export const deleteAdminAccount = (id) => api.delete(`/admin/admins/${id}`);
export const updateAdminPermissions = (id, permissions) => api.patch(`/admin/admins/${id}/permissions`, { permissions });
export const getSupportRoleDefaults = () => api.get("/admin/support-role-defaults");
export const updateSupportRoleDefaults = (permissions) => api.patch("/admin/support-role-defaults", { permissions });

// B2B — request access
export const requestB2BAccess = (data) => api.post("/api/v1/b2b/request-access", data);
export const createRecurringOrder = (data) => api.post("/api/v1/b2b/recurring-orders", data);
export const getRecurringOrders = () => api.get("/api/v1/b2b/recurring-orders");
export const updateRecurringOrder = (id, data) => api.patch(`/api/v1/b2b/recurring-orders/${id}`, data);

// Agent — "Upgrade to Agent" self-service application
export const requestAgentAccess = (data) => api.post("/users/me/agent-request", data);

// Fleet Owner — self-service application + own fleet roster
export const requestFleetOwnerAccess = (fleetName) => api.post("/fleet/request-access", { fleetName });
export const getMyFleet = () => api.get("/fleet/me/riders");

// Web push (browser notifications) — role-scoped since users and riders are
// separate accounts/models on the backend.
export const getVapidPublicKey = () => api.get("/public/vapid-public-key");
export const subscribeWebPush = (role, subscription) =>
  api.post(`/${role === "rider" ? "riders" : "users"}/me/web-push-subscribe`, { subscription });
export const unsubscribeWebPush = (role) =>
  api.post(`/${role === "rider" ? "riders" : "users"}/me/web-push-unsubscribe`);

// Screen Pop-up Cards (configurable by admin, displayed to users per screen)
export const getScreenPopups = () => api.get("/admin/screen-popups");
export const createScreenPopup = (data) => api.post("/admin/screen-popups", data);
export const updateScreenPopup = (id, data) => api.put(`/admin/screen-popups/${id}`, data);
export const toggleScreenPopup = (id) => api.patch(`/admin/screen-popups/${id}/toggle`);
export const deleteScreenPopup = (id) => api.delete(`/admin/screen-popups/${id}`);
export const getActiveScreenPopup = (screen) => api.get(`/public/screen-popups?screen=${screen || "dashboard"}`);

// Admin — Map Providers
export const getMapCredentials = () => api.get("/admin/map-providers/credentials");
export const createMapCredential = (data) => api.post("/admin/map-providers/credentials", data);
export const updateMapCredential = (id, data) => api.patch(`/admin/map-providers/credentials/${id}`, data);
export const deleteMapCredential = (id) => api.delete(`/admin/map-providers/credentials/${id}`);
export const testMapCredential = (id) => api.post(`/admin/map-providers/credentials/${id}/test`);
export const activateMapCredential = (id) => api.post(`/admin/map-providers/credentials/${id}/activate`);
export const getMapConfig = () => api.get("/admin/map-providers/config");
export const updateMapConfig = (data) => api.patch("/admin/map-providers/config", data);
export const getMapHealth = () => api.get("/admin/map-providers/health");
export const getMapAuditLogs = (limit = 50) => api.get(`/admin/map-providers/audit-log?limit=${limit}`);

export default api;

